"use client";

import { Children, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { FileText, Image as ImageIcon, Mail } from "lucide-react";
import { cn } from "@/lib/utils";
import { isSpecialtyChatLink } from "@/lib/specialtyChatLinks";
import { stripStudyReferences } from "@/lib/chatUtils";
import { extractSendEmailQuestions, type SendEmailQuestionData } from "@/lib/sendEmailQuestion";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { EmailComponent } from "./EmailComponent";
import { EmailPromptComponent } from "./EmailPromptComponent";
import { SaveChatPrompt } from "./SaveChatPrompt";
import { Attachment } from "@/lib/api";
import {
  containsMarkdownTable,
  isPotentialMarkdownTable,
  normalizeMarkdownTableSpacing,
} from "./markdownTables";
import {
  createStreamingRevealState,
  reconcileStreamingReveal,
  revealIdleTail,
  type DisplayedRevealBlock,
} from "./streamingReveal.ts";

interface ChatMessageProps {
  message: {
    id: string;
    role: "user" | "assistant";
    content: string;
    timestamp: Date;
    attachments?: Attachment[];
  };
  chatId?: string;
  sessionId?: string;
  isStreamingMessage?: boolean;
  onFirstStreamingReveal?: () => void;
  // This message's email card was sent before it rendered (see
  // isEmailMessageSent in lib/savePrompt.ts).
  emailSent?: boolean;
  onEmailSent?: () => void;
}

// Data types for parsed emails
interface EmailData {
  content: string;
  recipient?: string;
}

// Extract old-style <email> tags (backwards compatibility)
function extractEmails(content: string): { emails: EmailData[], cleanContent: string } {
  const emailRegex = /<email(?:\s+to="([^"]*)")?>([\s\S]*?)<\/email>/g;
  const emails: EmailData[] = [];
  let match;

  while ((match = emailRegex.exec(content)) !== null) {
    const recipient = match[1] || undefined;
    const emailContent = match[2].trim();
    // Only add non-empty emails
    if (emailContent) {
      emails.push({ content: emailContent, recipient });
    }
  }

  // Remove email tags and content from the main message, but preserve spacing
  const cleanContent = content.replace(emailRegex, '').trim();

  return { emails, cleanContent };
}

/** Whether an assistant message carries a send card for a study email. */
export function hasSendEmailQuestion(content: string): boolean {
  return extractSendEmailQuestions(content).sendEmailQuestions.length > 0;
}

// Detect partial email tag (opening tag present but no closing tag - email is streaming)
function detectPartialEmailTag(content: string): {
  isLoading: boolean;
  textBeforeTag: string;
} {
  // Check if we have an opening tag but no closing tag
  const hasOpeningTag = content.includes('<send_email_question');
  const hasClosingTag = content.includes('</send_email_question>');

  if (hasOpeningTag && !hasClosingTag) {
    // Extract text before the opening tag
    const tagStartIndex = content.indexOf('<send_email_question');
    const textBeforeTag = content.substring(0, tagStartIndex).trim();
    return { isLoading: true, textBeforeTag };
  }

  return { isLoading: false, textBeforeTag: content };
}

function preserveNumberedRecommendationBreaks(content: string): string {
  return content.replace(
    /([^\n])\n(?=(?:\*\*)?\d+\.\s+\S)/g,
    "$1  \n"
  );
}

function isBlankParagraph(children: ReactNode): boolean {
  return Children.toArray(children).every((child) => {
    if (typeof child === "string") {
      return child.trim() === "";
    }

    return false;
  });
}

interface MarkdownContentProps {
  content: string;
}

function MarkdownContent({ content }: MarkdownContentProps) {
  return (
    <div className="text-base text-foreground">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children }) => (
            <h1 className="mt-4 mb-2 text-xl font-semibold leading-snug text-foreground">
              {children}
            </h1>
          ),
          h2: ({ children }) => (
            <h2 className="mt-4 mb-2 text-lg font-semibold leading-snug text-foreground">
              {children}
            </h2>
          ),
          h3: ({ children }) => (
            <h3 className="mt-3 mb-2 text-base font-semibold leading-snug text-foreground">
              {children}
            </h3>
          ),
          p: ({ children }) =>
            isBlankParagraph(children) ? (
              <p className="h-1 leading-none" />
            ) : (
              <p className="leading-relaxed">{children}</p>
            ),
          ul: ({ children }) => (
            <ul className="list-disc space-y-1 pl-5">
              {children}
            </ul>
          ),
          ol: ({ children, start }) => (
            <ol
              className="list-decimal space-y-1 pl-5"
              start={start}
            >
              {children}
            </ol>
          ),
          li: ({ children }) => (
            <li className="pl-1 leading-relaxed marker:text-primary-600">
              {children}
            </li>
          ),
          strong: ({ children }) => (
            <strong className="font-semibold text-foreground">
              {children}
            </strong>
          ),
          code: ({ children }) => (
            <code className="rounded-sm bg-muted px-1 py-0.5 text-sm">
              {children}
            </code>
          ),
          pre: ({ children }) => (
            <pre className="my-3 max-w-full overflow-x-auto rounded-lg bg-muted p-3 text-sm">
              {children}
            </pre>
          ),
          a: ({ href, children }) => isSpecialtyChatLink(href) ? (
            <span>{children}</span>
          ) : (
            <a
              href={href}
              className="text-primary-600 underline"
              target="_blank"
              rel="noreferrer"
            >
              {children}
            </a>
          ),
          table: ({ children }) => (
            <div className="scrollbar-thin my-3 max-w-full overflow-x-auto overscroll-x-contain rounded-xl border border-[hsl(var(--border))] bg-background shadow-xs">
              <table className="w-max min-w-full border-collapse text-left text-sm">
                {children}
              </table>
            </div>
          ),
          thead: ({ children }) => (
            <thead className="bg-[hsl(var(--muted))] text-foreground">
              {children}
            </thead>
          ),
          tbody: ({ children }) => (
            <tbody className="divide-y divide-[hsl(var(--border))]">
              {children}
            </tbody>
          ),
          tr: ({ children }) => (
            <tr className="transition-colors even:bg-[hsl(var(--muted)/0.45)] hover:bg-[hsl(var(--muted)/0.75)]">
              {children}
            </tr>
          ),
          th: ({ children, align }) => (
            <th
              scope="col"
              className="max-w-80 whitespace-nowrap border-b border-[hsl(var(--border))] px-3 py-2.5 align-top font-semibold leading-snug"
              style={{ textAlign: align === "char" ? "left" : align || "left" }}
            >
              {children}
            </th>
          ),
          td: ({ children, align }) => (
            <td
              className="max-w-80 whitespace-normal wrap-break-word px-3 py-2.5 align-top leading-relaxed"
              style={{ textAlign: align === "char" ? "left" : align || "left" }}
            >
              {children}
            </td>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

const STREAM_IDLE_REVEAL_MS = 900;

interface CompletedRevealBlock {
  text: string;
  sourceEnd: number;
}

function isSingleLineMarkdownBlock(line: string): boolean {
  return /^\s*(?:#{1,6}\s+|[-*+]\s+|\d+\.\s+|>\s+)/.test(line);
}

const SENTENCE_BOUNDARY_PUNCTUATION = new Set([".", "!", "?", "。", "！", "？"]);
const SENTENCE_BOUNDARY_TRAILING_CHARS = new Set([
  "\"",
  "'",
  ")",
  "]",
  "}",
  "”",
  "’",
]);
const SENTENCE_BOUNDARY_ABBREVIATIONS = [
  "z.b.",
  "d.h.",
  "u.a.",
  "u. a.",
  "v.a.",
  "v. a.",
  "i.d.r.",
  "i. d. r.",
  "bzw.",
  "bspw.",
  "ca.",
  "circa.",
  "ggf.",
  "evtl.",
  "etc.",
  "usw.",
  "inkl.",
  "zzgl.",
  "bsp.",
  "nr.",
  "abs.",
  "art.",
  "dr.",
  "prof.",
  "med.",
  "e.g.",
  "i.e.",
  "vs.",
  "mr.",
  "mrs.",
  "ms.",
  "no.",
  "fig.",
  "ref.",
];
const GERMAN_MONTH_NAMES = [
  "januar",
  "februar",
  "märz",
  "maerz",
  "april",
  "mai",
  "juni",
  "juli",
  "august",
  "september",
  "oktober",
  "november",
  "dezember",
];
const MARKDOWN_EMPHASIS_MARKERS = new Set(["*", "_"]);

function normalizeSentenceBoundaryText(value: string): string {
  return value.toLowerCase().replace(/\s+/g, "");
}

function isKnownSentenceBoundaryAbbreviation(prefix: string): boolean {
  const normalizedPrefix = normalizeSentenceBoundaryText(prefix);
  return SENTENCE_BOUNDARY_ABBREVIATIONS.some((abbreviation) => {
    const normalizedAbbreviation = normalizeSentenceBoundaryText(abbreviation);
    return normalizedPrefix.endsWith(normalizedAbbreviation);
  });
}

function isInsideKnownSentenceBoundaryAbbreviation(
  text: string,
  index: number
): boolean {
  const windowStart = Math.max(0, index - 16);
  const windowText = text.slice(windowStart, Math.min(text.length, index + 16));
  const compactCharacters: Array<{ character: string; sourceIndex: number }> = [];

  for (let offset = 0; offset < windowText.length; offset += 1) {
    const character = windowText[offset];

    if (/\s/.test(character)) {
      continue;
    }

    compactCharacters.push({
      character: character.toLowerCase(),
      sourceIndex: windowStart + offset,
    });
  }

  const compactText = compactCharacters
    .map(({ character }) => character)
    .join("");

  return SENTENCE_BOUNDARY_ABBREVIATIONS.some((abbreviation) => {
    const normalizedAbbreviation = normalizeSentenceBoundaryText(abbreviation);
    let matchIndex = compactText.indexOf(normalizedAbbreviation);

    while (matchIndex !== -1) {
      const matchEndIndex = matchIndex + normalizedAbbreviation.length - 1;
      const sourceStartIndex = compactCharacters[matchIndex]?.sourceIndex;
      const sourceEndIndex = compactCharacters[matchEndIndex]?.sourceIndex;

      if (
        sourceStartIndex !== undefined &&
        sourceEndIndex !== undefined &&
        sourceStartIndex <= index &&
        index <= sourceEndIndex
      ) {
        return true;
      }

      matchIndex = compactText.indexOf(
        normalizedAbbreviation,
        matchIndex + 1
      );
    }

    return false;
  });
}

function isNumberAtRevealBoundary(text: string, numberPrefixIndex: number): boolean {
  let boundaryIndex = numberPrefixIndex;

  while (
    boundaryIndex >= 0 &&
    MARKDOWN_EMPHASIS_MARKERS.has(text[boundaryIndex] ?? "")
  ) {
    boundaryIndex -= 1;
  }

  const characterBeforeNumber = text[boundaryIndex] ?? "";
  return boundaryIndex < 0 || /\s|[([]/.test(characterBeforeNumber);
}

function isOrderedListMarkerPeriod(text: string, index: number): boolean {
  if (text[index] !== ".") {
    return false;
  }

  let numberStartIndex = index - 1;
  while (numberStartIndex >= 0 && /\d/.test(text[numberStartIndex])) {
    numberStartIndex -= 1;
  }

  const hasNumberBeforePeriod = numberStartIndex < index - 1;
  const characterAfterPeriod = text[index + 1] ?? "";

  return (
    hasNumberBeforePeriod &&
    isNumberAtRevealBoundary(text, numberStartIndex) &&
    /\s/.test(characterAfterPeriod)
  );
}

function isGermanDateDayPeriod(text: string, index: number): boolean {
  if (text[index] !== ".") {
    return false;
  }

  let dayStartIndex = index - 1;
  while (dayStartIndex >= 0 && /\d/.test(text[dayStartIndex])) {
    dayStartIndex -= 1;
  }

  const dayText = text.slice(dayStartIndex + 1, index);
  if (!/^\d{1,2}$/.test(dayText)) {
    return false;
  }

  if (!isNumberAtRevealBoundary(text, dayStartIndex)) {
    return false;
  }

  const textAfterPeriod = text.slice(index + 1).trimStart().toLowerCase();
  return GERMAN_MONTH_NAMES.some((monthName) =>
    textAfterPeriod.startsWith(monthName)
  );
}

function isSentenceBoundaryAt(text: string, index: number): boolean {
  const character = text[index];

  if (!SENTENCE_BOUNDARY_PUNCTUATION.has(character)) {
    return false;
  }

  if (character === ".") {
    const previousCharacter = text[index - 1] ?? "";
    const nextCharacter = text[index + 1] ?? "";

    if (isInsideKnownSentenceBoundaryAbbreviation(text, index)) {
      return false;
    }

    if (isOrderedListMarkerPeriod(text, index)) {
      return false;
    }

    if (isGermanDateDayPeriod(text, index)) {
      return false;
    }

    if (/\d/.test(previousCharacter) && /\d/.test(nextCharacter)) {
      return false;
    }

    if (
      /[A-Za-zÄÖÜäöüß]/.test(previousCharacter) &&
      /[A-Za-zÄÖÜäöüß]/.test(nextCharacter)
    ) {
      return false;
    }

    if (isKnownSentenceBoundaryAbbreviation(text.slice(0, index + 1))) {
      return false;
    }

    if (/\b[A-ZÄÖÜ]\.$/.test(text.slice(0, index + 1).trimEnd())) {
      return false;
    }
  }

  let nextIndex = index + 1;
  while (SENTENCE_BOUNDARY_TRAILING_CHARS.has(text[nextIndex] ?? "")) {
    nextIndex += 1;
  }

  return nextIndex >= text.length || /\s/.test(text[nextIndex]);
}

function splitSentenceRevealUnits(block: string): string[] {
  const units: string[] = [];
  let startIndex = 0;

  for (let index = 0; index < block.length; index += 1) {
    if (!isSentenceBoundaryAt(block, index)) {
      continue;
    }

    let endIndex = index + 1;
    while (SENTENCE_BOUNDARY_TRAILING_CHARS.has(block[endIndex] ?? "")) {
      endIndex += 1;
    }
    while (endIndex < block.length && /\s/.test(block[endIndex])) {
      endIndex += 1;
    }

    units.push(block.slice(startIndex, endIndex));
    startIndex = endIndex;
    index = endIndex - 1;
  }

  if (startIndex < block.length) {
    units.push(block.slice(startIndex));
  }

  return units.filter((unit) => unit.trim() !== "");
}

function getCompletedRevealBlocks(
  content: string,
  holdTrailingTableCandidate: boolean
): CompletedRevealBlock[] {
  const blocks: CompletedRevealBlock[] = [];
  let position = 0;
  let paragraphLines: string[] = [];
  let isInsideFence = false;
  let fenceBuffer: string[] = [];

  const flushParagraph = (sourceEnd: number) => {
    if (paragraphLines.length > 0) {
      blocks.push({
        text: paragraphLines.join("\n").trim(),
        sourceEnd,
      });
      paragraphLines = [];
    }
  };

  const flushFence = (sourceEnd: number) => {
    if (fenceBuffer.length > 0) {
      blocks.push({
        text: fenceBuffer.join("\n"),
        sourceEnd,
      });
      fenceBuffer = [];
    }
  };

  while (position < content.length) {
    const newlineIndex = content.indexOf("\n", position);
    const hasCompleteLine = newlineIndex !== -1;

    if (!hasCompleteLine) {
      break;
    }

    const sourceEnd = newlineIndex + 1;
    const line = content.slice(position, newlineIndex);
    const trimmedLine = line.trim();
    const isFenceBoundary = trimmedLine.startsWith("```");

    if (isInsideFence) {
      fenceBuffer.push(line);

      if (isFenceBoundary) {
        isInsideFence = false;
        flushFence(sourceEnd);
      }

      position = sourceEnd;
      continue;
    }

    if (isFenceBoundary) {
      flushParagraph(position);
      isInsideFence = true;
      fenceBuffer = [line];
      position = sourceEnd;
      continue;
    }

    if (trimmedLine === "") {
      flushParagraph(sourceEnd);
      position = sourceEnd;
      continue;
    }

    if (isSingleLineMarkdownBlock(line)) {
      flushParagraph(position);
      blocks.push({ text: line, sourceEnd });
      position = sourceEnd;
      continue;
    }

    paragraphLines.push(line);
    position = sourceEnd;
  }

  const trailingBlock = blocks[blocks.length - 1];
  if (
    holdTrailingTableCandidate &&
    trailingBlock &&
    content.slice(trailingBlock.sourceEnd).trim() === "" &&
    isPotentialMarkdownTable(trailingBlock.text)
  ) {
    blocks.pop();
  }

  return blocks;
}

function splitRevealUnits(block: string): string[] {
  const trimmedBlock = block.trim();

  if (!trimmedBlock) {
    return [];
  }

  if (trimmedBlock.startsWith("```")) {
    return [block];
  }

  if (containsMarkdownTable(block)) {
    return [block];
  }

  const lines = block.split("\n").filter((line) => line.trim() !== "");
  const isLineBasedBlock =
    lines.length > 1 || lines.every((line) => isSingleLineMarkdownBlock(line));

  if (isLineBasedBlock) {
    return lines;
  }

  const sentenceUnits = splitSentenceRevealUnits(block);
  return sentenceUnits && sentenceUnits.length > 0 ? sentenceUnits : [block];
}

interface DiffuseRevealBlockProps {
  block: DisplayedRevealBlock;
  reduceMotion: boolean;
}

function DiffuseRevealBlock({ block, reduceMotion }: DiffuseRevealBlockProps) {
  const units = useMemo(() => splitRevealUnits(block.text), [block.text]);

  if (units.length === 0) {
    return null;
  }

  return (
    <motion.div
      initial={
        reduceMotion
          ? false
          : { opacity: 0, y: 6, filter: "blur(8px)" }
      }
      animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
      transition={{ duration: 0.65, ease: "easeOut" }}
      className="space-y-1 sm:space-y-1.5"
    >
      {units.map((unit, index) => (
        <motion.div
          key={`${block.id}-${index}`}
          initial={reduceMotion ? false : { opacity: 0, filter: "blur(5px)" }}
          animate={{ opacity: 1, filter: "blur(0px)" }}
          transition={{
            duration: 0.55,
            delay: reduceMotion ? 0 : index * 0.11,
            ease: "easeOut",
          }}
        >
          <MarkdownContent content={unit} />
        </motion.div>
      ))}
    </motion.div>
  );
}

interface StreamingMarkdownProps {
  content: string;
  isStreaming: boolean;
  onFirstReveal?: () => void;
}

function StreamingMarkdown({
  content,
  isStreaming,
  onFirstReveal,
}: StreamingMarkdownProps) {
  const shouldReduceMotion = useReducedMotion();
  const [revealState, setRevealState] = useState(createStreamingRevealState);
  const hasNotifiedFirstRevealRef = useRef(false);
  const notificationGenerationRef = useRef(revealState.generation);
  const onFirstRevealRef = useRef(onFirstReveal);

  useEffect(() => {
    onFirstRevealRef.current = onFirstReveal;
  }, [onFirstReveal]);

  useEffect(() => {
    setRevealState((previousState) =>
      reconcileStreamingReveal(
        previousState,
        content,
        isStreaming,
        getCompletedRevealBlocks
      )
    );

    if (!isStreaming) {
      return;
    }

    const idleTimer = setTimeout(() => {
      setRevealState((previousState) =>
        revealIdleTail(previousState, content)
      );
    }, STREAM_IDLE_REVEAL_MS);

    return () => {
      clearTimeout(idleTimer);
    };
  }, [content, isStreaming]);

  useEffect(() => {
    if (notificationGenerationRef.current !== revealState.generation) {
      notificationGenerationRef.current = revealState.generation;
      hasNotifiedFirstRevealRef.current = false;
    }

    if (
      revealState.blocks.length === 0 ||
      hasNotifiedFirstRevealRef.current
    ) {
      return;
    }

    hasNotifiedFirstRevealRef.current = true;
    onFirstRevealRef.current?.();
  }, [revealState.blocks, revealState.generation]);

  return (
    <div className="space-y-2 sm:space-y-2.5">
      {revealState.blocks.map((block) => (
        <DiffuseRevealBlock
          key={block.id}
          block={block}
          reduceMotion={Boolean(shouldReduceMotion)}
        />
      ))}
    </div>
  );
}

// Loading placeholder component shown while email is being generated
function EmailLoadingPlaceholder() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl bg-(--outer-bg) border border-border shadow-xs p-4"
    >
      <div className="flex items-center gap-3">
        <div className="shrink-0 w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
          <Mail className="h-5 w-5 text-primary-600 animate-pulse" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-medium text-foreground">
            E-Mail wird erstellt...
          </p>
          <div className="flex gap-1 mt-1">
            <span className="h-1.5 w-1.5 bg-primary-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
            <span className="h-1.5 w-1.5 bg-primary-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
            <span className="h-1.5 w-1.5 bg-primary-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export function ChatMessage({
  message,
  chatId,
  sessionId,
  isStreamingMessage = false,
  onFirstStreamingReveal,
  emailSent = false,
  onEmailSent,
}: ChatMessageProps) {
  const isUser = message.role === "user";
  // Email cards of this message sent on this page, by index.
  const [sentEmailIndexes, setSentEmailIndexes] = useState<number[]>([]);
  const [hasRenderedAsStreaming, setHasRenderedAsStreaming] = useState(
    isStreamingMessage
  );

  useEffect(() => {
    if (isStreamingMessage) {
      setHasRenderedAsStreaming(true);
    }
  }, [isStreamingMessage]);

  // Extract both old and new email formats from assistant messages
  let emails: EmailData[] = [];
  let sendEmailQuestions: SendEmailQuestionData[] = [];
  let cleanContent = message.content;
  let isEmailLoading = false;

  if (!isUser) {
    cleanContent = stripStudyReferences(cleanContent);

    // First check if we have a partial email tag (streaming state)
    const partialTagResult = detectPartialEmailTag(cleanContent);
    isEmailLoading = partialTagResult.isLoading;

    if (isEmailLoading) {
      // If loading, only show content before the tag
      cleanContent = partialTagResult.textBeforeTag;
    } else {
      // Not loading - extract complete tags normally
      // First extract new format
      const sendEmailResult = extractSendEmailQuestions(cleanContent);
      sendEmailQuestions = sendEmailResult.sendEmailQuestions;
      cleanContent = sendEmailResult.cleanContent;

      // Then extract old format
      const emailResult = extractEmails(cleanContent);
      emails = emailResult.emails;
      cleanContent = emailResult.cleanContent;
    }
  }

  const tableNormalizedContent = normalizeMarkdownTableSpacing(cleanContent);
  const markdownContent = preserveNumberedRecommendationBreaks(
    tableNormalizedContent
  );
  const processedContent = markdownContent.replace(
    /\n\n(?!\s*(?:\d+\.\s|[-*+]\s))/g,
    "\n\n&nbsp;\n\n"
  );
  const shouldUseStreamingRenderer = isStreamingMessage || hasRenderedAsStreaming;

  return (
    <div className="space-y-3">
      {/* Main chat message */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        className={cn("flex", isUser ? "justify-end" : "justify-start")}
      >
        <div
          className={cn(
            isUser
              ? "max-w-full sm:max-w-[75%] rounded-2xl px-4 py-1 shadow-xs bg-(--outer-bg) text-foreground"
              : "w-full"
          )}
        >
          {/* Attachment indicators - shows above message content */}
          {message.attachments && message.attachments.length > 0 && (
            <div className="mb-2 space-y-2">
              {message.attachments.map((attachment, index) => (
                <div
                  key={`${message.id}-attachment-${index}`}
                  className="flex items-center gap-2 p-2 bg-[#6D4AFF]/10 rounded-lg border border-[#6D4AFF]/20"
                >
                  {attachment.type === 'image' ? (
                    <ImageIcon className="h-4 w-4 text-[#6D4AFF] shrink-0" />
                  ) : (
                    <FileText className="h-4 w-4 text-[#6D4AFF] shrink-0" />
                  )}
                  <span className="text-xs text-gray-700 truncate flex-1">
                    {attachment.filename}
                  </span>
                  <span className="text-xs text-gray-500 shrink-0">
                    {(attachment.size / 1024).toFixed(1)} KB
                  </span>
                </div>
              ))}
            </div>
          )}

          {isUser ? (
            <p className="text-base leading-loose whitespace-pre-wrap">
              {message.content}
            </p>
          ) : (
            <>
              {cleanContent ? (
                <div className="text-base">
                  {shouldUseStreamingRenderer ? (
                    <StreamingMarkdown
                      content={tableNormalizedContent}
                      isStreaming={isStreamingMessage}
                      onFirstReveal={onFirstStreamingReveal}
                    />
                  ) : (
                    <MarkdownContent content={processedContent} />
                  )}
                </div>
              ) : (
                !message.content && (
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1">
                      <span className="h-2 w-2 bg-primary-400 rounded-full animate-pulse"></span>
                      <span className="h-2 w-2 bg-primary-400 rounded-full animate-pulse delay-75"></span>
                      <span className="h-2 w-2 bg-primary-400 rounded-full animate-pulse delay-150"></span>
                    </div>
                    <span className="text-sm text-muted-foreground">
                      AI is thinking
                    </span>
                  </div>
                )
              )}
            </>
          )}
        </div>
      </motion.div>

      {/* Email loading placeholder - shown while email is being streamed */}
      {isEmailLoading && (
        <div className="flex justify-start">
          <div className="w-6 sm:w-8 shrink-0" />
          <div className="flex-1 max-w-[95%] sm:max-w-[85%] md:max-w-[80%]">
            <EmailLoadingPlaceholder />
          </div>
        </div>
      )}

      {/* New send_email_question components (with direct send capability) */}
      {sendEmailQuestions.length > 0 && (
        <div className="space-y-2 sm:space-y-3">
          {sendEmailQuestions.map((emailQuestion, index) => (
            <motion.div
              key={`${message.id}-send-email-${index}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, ease: "easeOut", delay: 0.1 * (index + 1) }}
              className="flex justify-start"
            >
              <div className="w-6 sm:w-8 shrink-0" /> {/* Spacer to align with message */}
              <div className="flex-1 max-w-[95%] sm:max-w-[85%] md:max-w-[80%]">
                <EmailPromptComponent
                  emailContent={emailQuestion.content}
                  recipientEmail={emailQuestion.recipient}
                  patientEmail={emailQuestion.patientEmail}
                  patientName={emailQuestion.patientName}
                  referral={emailQuestion.referral}
                  chatId={chatId}
                  sessionId={sessionId}
                  initiallySent={emailSent}
                  onSent={() => {
                    setSentEmailIndexes((prev) =>
                      prev.includes(index) ? prev : [...prev, index]
                    );
                    onEmailSent?.();
                  }}
                />
              </div>
            </motion.div>
          ))}

          {/* The save card goes below the email cards once one is sent. It
              is UI only and never part of the chat's messages. */}
          {(emailSent || sentEmailIndexes.length > 0) && (
            <div className="flex justify-start">
              <div className="w-6 sm:w-8 shrink-0" /> {/* Spacer to align with message */}
              <div className="flex-1 max-w-[95%] sm:max-w-[85%] md:max-w-[80%]">
                <SaveChatPrompt chatId={chatId} sessionId={sessionId} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Old-style email components (backwards compatibility) */}
      {emails.length > 0 && (
        <div className="space-y-2 sm:space-y-3">
          {emails.map((email, index) => (
            <motion.div
              key={`${message.id}-email-${index}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, ease: "easeOut", delay: 0.1 * (index + 1) }}
              className="flex justify-start"
            >
              <div className="w-6 sm:w-8 shrink-0" /> {/* Spacer to align with message - smaller on mobile */}
              <div className="flex-1 max-w-[95%] sm:max-w-[85%] md:max-w-[80%]">
                <EmailComponent
                  emailContent={email.content}
                  recipientEmail={email.recipient}
                  chatId={chatId}
                  sessionId={sessionId}
                />
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
